import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

export interface Slide {
  src: string;
  srcSet?: string;
  alt?: string;
  position?: string;
}

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function WeddingSlideshow({ slides }: { slides: Slide[] }) {
  const validSlides = useMemo(() => slides.filter((slide) => slide.src), [slides]);
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(() => !prefersReducedMotion());
  const [failed, setFailed] = useState<Set<number>>(() => new Set());

  const usableIndexes = useMemo(
    () => validSlides.map((_, index) => index).filter((index) => !failed.has(index)),
    [failed, validSlides],
  );

  const move = useCallback(
    (direction: 1 | -1) => {
      if (usableIndexes.length < 2) return;
      const currentPosition = Math.max(0, usableIndexes.indexOf(active));
      const nextPosition =
        (currentPosition + direction + usableIndexes.length) % usableIndexes.length;
      setActive(usableIndexes[nextPosition]);
    },
    [active, usableIndexes],
  );

  useEffect(() => {
    if (!playing || usableIndexes.length < 2) return;
    const timer = window.setInterval(() => move(1), 7000);
    return () => window.clearInterval(timer);
  }, [move, playing, usableIndexes.length]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) setPlaying(false);
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  useEffect(() => {
    if (failed.has(active) && usableIndexes.length) setActive(usableIndexes[0]);
  }, [active, failed, usableIndexes]);

  return (
    <div
      className={`slideshow ${usableIndexes.length === 0 ? 'slideshow--fallback' : ''}`}
      aria-label="Photos d'Orry et Rudy"
    >
      {validSlides.map((slide, index) => (
        <picture
          className={`slideshow__slide ${active === index && !failed.has(index) ? 'is-active' : ''}`}
          key={`${slide.src}-${index}`}
          aria-hidden="true"
        >
          {slide.srcSet && <source type="image/webp" srcSet={slide.srcSet} />}
          <img
            src={slide.src}
            alt=""
            width="1080"
            height="1440"
            loading={index === 0 ? 'eager' : 'lazy'}
            fetchPriority={index === 0 ? 'high' : 'auto'}
            style={{ objectPosition: slide.position || 'center' }}
            onError={() => setFailed((current) => new Set(current).add(index))}
          />
        </picture>
      ))}
      <div className="slideshow__veil" />
      {usableIndexes.length > 1 && (
        <div className="slideshow__controls">
          <button type="button" onClick={() => move(-1)} aria-label="Photo précédente">
            <ChevronLeft aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => setPlaying((value) => !value)}
            aria-label={playing ? 'Mettre le diaporama en pause' : 'Lire le diaporama'}
            aria-pressed={playing}
          >
            {playing ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
          </button>
          <button type="button" onClick={() => move(1)} aria-label="Photo suivante">
            <ChevronRight aria-hidden="true" />
          </button>
        </div>
      )}
      <div className="slideshow__progress" aria-hidden="true">
        {usableIndexes.map((index) => (
          <span key={index} className={active === index ? 'is-active' : ''} />
        ))}
      </div>
    </div>
  );
}
